import { useRef } from 'react';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import {
  ChevronRightIcon,
  FavoriteToggle,
  IconButtonV2,
  MeatballsIcon,
  OverlayPopover,
  Popover,
  TopicLibraryCard,
} from '@utilities';
import styles from '../../_index.module.scss';

export interface ProjectItemProps {
  handleReadProject: (sessionId: string) => void;
  handleSelectProject: (sessionId: string) => void;
  handleToggleFavorite: ({ isFavorite, name, projectSessionId }: any) => void;
  isActive: boolean;
  isFavorite: boolean;
  projectId: string;
  projectName: string;
  projectSessionId: string;
  setIsDeleteModalActive: (isActive: boolean) => void;
  setIsRenameModalActive: (isActive: boolean) => void;
}

const ProjectItem = ({
  handleReadProject,
  handleSelectProject,
  handleToggleFavorite,
  isActive = false,
  isFavorite = false,
  projectId,
  projectName,
  projectSessionId,
  setIsDeleteModalActive,
  setIsRenameModalActive,
}: ProjectItemProps) => {
  const [isPopoverActive, setIsPopoverActive] = useState(false);
  const meatballButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenProject = () => {
    handleReadProject(projectSessionId);
  };

  const titleAccessory = (
    <div
      className={styles['project-item-actions']}
      data-testid={`project-item-actions-${projectSessionId}`}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
    >
      <div className={styles['project-item-more']}>
        <IconButtonV2
          aria-label="More Actions"
          className={styles['project-item-meatball']}
          data-testid={`project-item-more-actions-${projectSessionId}`}
          icon={<MeatballsIcon />}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            handleSelectProject(projectSessionId);
            setIsPopoverActive(true);
          }}
          ref={meatballButtonRef}
          size="small"
          variant="subtle"
        />
        <OverlayPopover
          hasArrow
          isActive={isPopoverActive}
          onHide={() => {
            setIsPopoverActive(false);
          }}
          target={meatballButtonRef.current}
        >
          <Popover.Menu>
            <Popover.Item
              aria-label="Rename Project"
              data-testid={`project-item-rename-${projectSessionId}`}
              onClick={(event: MouseEvent<HTMLElement>) => {
                event?.stopPropagation?.();
                handleSelectProject(projectSessionId);
                setIsRenameModalActive(true);
                setIsPopoverActive(false);
              }}
            >
              <p>Rename</p>
            </Popover.Item>
            <Popover.Item
              aria-label="Delete Project"
              data-testid={`project-item-delete-${projectSessionId}`}
              onClick={(event: MouseEvent<HTMLElement>) => {
                event?.stopPropagation?.();
                handleSelectProject(projectSessionId);
                setIsDeleteModalActive(true);
                setIsPopoverActive(false);
              }}
            >
              <p>Delete</p>
            </Popover.Item>
          </Popover.Menu>
        </OverlayPopover>
      </div>

      <IconButtonV2
        aria-label="Open Project"
        className={styles['project-item-open']}
        data-testid={`project-item-open-${projectSessionId}`}
        icon={<ChevronRightIcon />}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          handleOpenProject();
        }}
        size="small"
        variant="subtle"
      />
    </div>
  );

  return (
    <TopicLibraryCard
      className={styles['project-item']}
      data-testid={`project-item-card-${projectSessionId}`}
      icon={
        <button
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          className={styles['project-favorite-container']}
          data-testid={`project-item-favorite-${projectSessionId}`}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            handleToggleFavorite({
              isFavorite: isFavorite,
              name: projectName,
              projectSessionId: projectSessionId,
            });
          }}
          type="button"
        >
          <FavoriteToggle isFavorite={isFavorite} />
        </button>
      }
      isActive={isActive}
      key={projectId}
      onClick={handleOpenProject}
      title={<span className={styles['project-item-title']}>{projectName}</span>}
      titleAccessory={titleAccessory}
      variant="dark"
    />
  );
};
export default ProjectItem;
